import { Injectable, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { Observable, firstValueFrom, map } from 'rxjs';
import { environment } from '../../environments/environment';
import { checkTokenExpiration } from '../utils';
import { ExternalAuth } from './auth-routing';
import {
  ExternalSignInDialogComponent,
  IExternalSignInDialogData,
} from '../shared/components/external-sign-in-dialog/external-sign-in-dialog.component';

// Kept apart from AuthService's 'accessToken'. AuthService.logout()
// clears all of localStorage, so signing out of this panel drops it too.
const TOKEN_KEY = 'externalAccessToken';

/**
 * Token for `apiEndpointExternal` when it is another origin (the Flix and
 * Train admins importing from Hunter). Used only by the interceptor, see
 * auth-routing.ts.
 */
@Injectable({ providedIn: 'root' })
export class ExternalAuthService implements ExternalAuth {
  readonly host = new URL(environment.apiEndpointExternal, location.href).host;
  // Bypasses the interceptors: the login call must carry neither token,
  // and a wrong password must not start another sign-in.
  private readonly http = new HttpClient(inject(HttpBackend));
  private readonly dialog = inject(MatDialog);
  private pending: Promise<string> | null = null;

  getToken(): string {
    const token = localStorage.getItem(TOKEN_KEY) ?? '';
    return token && !checkTokenExpiration(token) ? token : '';
  }

  clearToken(token: string): void {
    if (localStorage.getItem(TOKEN_KEY) === token) {
      localStorage.removeItem(TOKEN_KEY);
    }
  }

  askToSignIn(): Promise<string> {
    // Requests fired together (an import loads several lists) share one
    // dialog; one failing after the admin has signed in takes the new token.
    if (!this.pending) {
      const token = this.getToken();
      if (token) {
        return Promise.resolve(token);
      }
      const data: IExternalSignInDialogData = {
        host: this.host,
        signIn: (email, password) => this.signIn(email, password),
      };
      const ref = this.dialog.open(ExternalSignInDialogComponent, { data, restoreFocus: true });
      this.pending = firstValueFrom(ref.afterClosed())
        .then(() => this.getToken())
        .finally(() => { this.pending = null; });
    }
    return this.pending;
  }

  private signIn(email: string, password: string): Observable<void> {
    return this.http.put<{ token: string }>(
      `${environment.apiEndpointExternal}/auth/login`,
      { email, password },
      { headers: { Accept: 'application/json' } },
    ).pipe(map(res => localStorage.setItem(TOKEN_KEY, res.token)));
  }
}
